import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

/**
 * A big drop-down with a search box at the top: tapping it opens a
 * list (full width on a phone), typing narrows it. For long lists such
 * as Nigeria's 37 states or a state's local government areas.
 *
 *   options   [{ value, label }]
 *   value     the chosen value ('' for none)
 */
export default function SearchSelect({
  value, onChange, options, placeholder, title, searchPlaceholder, emptyText,
  disabled = false, testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const chosen = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, q]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="w-full h-14 rounded-md border border-input bg-white px-3 flex items-center justify-between gap-2 text-base text-left disabled:opacity-50"
        data-testid={testId}
      >
        <span className={`truncate ${chosen ? '' : 'text-muted-foreground'}`}>{chosen ? chosen.label : placeholder}</span>
        <ChevronDown className="w-5 h-5 shrink-0 opacity-60" />
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
        <DialogContent className="bg-white p-0 gap-0 max-h-[85vh] flex flex-col w-[calc(100%-1rem)] rounded-2xl">
          <DialogTitle className="px-4 pt-4 pb-2 text-lg font-heading">{title || placeholder}</DialogTitle>
          <div className="px-4 pb-3 border-b">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-12 rounded-xl border border-gray-300 pl-10 pr-3 text-base"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
          </div>
          <ul className="overflow-y-auto py-1" role="listbox">
            {shown.length === 0 && <li className="px-4 py-4 text-gray-500">{emptyText}</li>}
            {shown.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onClick={() => pick(o.value)}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-base text-left hover:bg-farm-primary/5 ${
                    o.value === value ? 'font-semibold text-farm-primary' : ''}`}
                >
                  <span className="break-words">{o.label}</span>
                  {o.value === value && <Check className="w-5 h-5 shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
